import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { TopProducts as TopProductsModel } from '@/api/models';
import { TopProductsSkeleton } from './TopProductsSkeleton';
import { TopProductsView } from './TopProductsView';
import { useTopProducts } from './useTopProducts';

type TopProductsProps = {
    'data-testid'?: string;
    id?: string;
};

type TopProductsContentProps = {
    data: TopProductsModel | null;
    'data-testid': string;
    error: ErrorResponse | null;
    id: string;
    isLoading: boolean;
};

/**
 * Self-served top products widget.
 *
 * Owns its own data hook (`useTopProducts`, which reads the live endpoint
 * through `useTopProductsQuery`) and is wrapped in an `ErrorBoundary` so a
 * render-time failure cannot crash the sales page. While the data is loading
 * the container renders `TopProductsSkeleton`, which mirrors the final layout
 * to prevent shift.
 */
export const TopProducts: React.FC<TopProductsProps> = ({
    'data-testid': dataTestId = 'top-products',
    id = 'top-products',
}) => {
    const { data, error, isLoading, onRetry } = useTopProducts();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('sales.widgets.topProducts.errorTitle'),
            errorMessage: t('sales.widgets.topProducts.errorMessage'),
            errorRetry: t('sales.widgets.topProducts.errorRetry'),
        }),
        [t],
    );

    return (
        <ErrorBoundary
            data-testid={`${dataTestId}-error-boundary`}
            fallbackMessage={copy.errorMessage}
            id={`${id}-error-boundary`}
            onRetry={onRetry}
            resetKeys={[error]}
            retryLabel={copy.errorRetry}
            title={copy.errorTitle}
        >
            <TopProductsContent
                data={data}
                data-testid={dataTestId}
                error={error}
                id={id}
                isLoading={isLoading}
            />
        </ErrorBoundary>
    );
};

/**
 * Inner component sits *inside* the `ErrorBoundary` so the `throw` below is
 * caught by it — React error boundaries do not catch errors thrown by the
 * component that renders them, only by their children.
 */
const TopProductsContent: React.FC<TopProductsContentProps> = ({
    data,
    'data-testid': dataTestId,
    error,
    id,
    isLoading,
}) => {
    if (error) {
        throw error;
    }

    if (isLoading || data === null) {
        return <TopProductsSkeleton />;
    }

    return <TopProductsView data={data} data-testid={dataTestId} id={id} />;
};
